import React, { useState, useEffect } from 'react';
import { X, Globe, Users, Lock, Loader2, UserPlus, Trash2 } from 'lucide-react';
import api from '../api';
import toast from 'react-hot-toast';

interface ProjectSharingModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: { id: number; name: string; visibility?: string; shared_with?: number[] } | null;
  onUpdate: () => void;
}

interface TenantUser {
  id: number;
  name: string;
  email: string;
}

const VISIBILITY_OPTIONS = [
  {
    value: 'private',
    label: 'Private',
    description: 'Only you can see this project',
    icon: Lock,
    color: 'text-gray-600',
    bg: 'bg-gray-50 border-gray-200',
    activeBg: 'bg-gray-100 border-gray-400 ring-1 ring-gray-400',
  },
  {
    value: 'shared',
    label: 'Shared',
    description: 'Specific team members can access',
    icon: Users,
    color: 'text-blue-600',
    bg: 'bg-blue-50 border-blue-200',
    activeBg: 'bg-blue-100 border-blue-400 ring-1 ring-blue-400',
  },
  {
    value: 'tenant',
    label: 'Everyone in Organization',
    description: 'All users in your organization can access',
    icon: Globe,
    color: 'text-green-600',
    bg: 'bg-green-50 border-green-200',
    activeBg: 'bg-green-100 border-green-400 ring-1 ring-green-400',
  },
];

const ProjectSharingModal: React.FC<ProjectSharingModalProps> = ({ isOpen, onClose, project, onUpdate }) => {
  const [visibility, setVisibility] = useState<string>('private');
  const [sharedWith, setSharedWith] = useState<number[]>([]);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [saving, setSaving] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (isOpen && project) {
      setVisibility(project.visibility || 'private');
      setSharedWith(project.shared_with || []);
      fetchTenantUsers();
    }
  }, [isOpen, project]);

  const fetchTenantUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await api.get('/api/project-management/users');
      const users = res.data.users || res.data.data || res.data || [];
      setTenantUsers(Array.isArray(users) ? users : []);
    } catch {
      setTenantUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSave = async () => {
    if (!project) return;
    setSaving(true);
    try {
      await api.put(`/api/projects/${project.id}`, {
        visibility,
        shared_with: visibility === 'shared' ? sharedWith : [],
      });
      toast.success(
        visibility === 'private' ? 'Project is now private'
          : visibility === 'tenant' ? 'Project shared with entire organization'
          : `Project shared with ${sharedWith.length} member${sharedWith.length !== 1 ? 's' : ''}`
      );
      onUpdate();
      onClose();
    } catch {
      toast.error('Failed to update sharing settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleUser = (userId: number) => {
    setSharedWith(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Share Project</h2>
            <p className="text-xs text-gray-500 mt-0.5">{project.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Visibility options */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Who can access this project?</label>
            {VISIBILITY_OPTIONS.map(opt => {
              const Icon = opt.icon;
              const isActive = visibility === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setVisibility(opt.value)}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg border transition-all text-left ${
                    isActive ? opt.activeBg : `${opt.bg} hover:border-gray-300`
                  }`}
                >
                  <Icon className={`w-5 h-5 mt-0.5 ${opt.color}`} />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{opt.label}</div>
                    <div className="text-xs text-gray-500">{opt.description}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* User selector (only for 'shared' visibility) */}
          {visibility === 'shared' && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <UserPlus className="w-4 h-4" />
                Select team members ({sharedWith.length} selected)
              </label>
              {loadingUsers ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-100">
                  {tenantUsers.map(user => {
                    const isSelected = sharedWith.includes(user.id);
                    return (
                      <button
                        key={user.id}
                        onClick={() => toggleUser(user.id)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors ${
                          isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div>
                          <div className="text-sm font-medium text-gray-800">{user.name}</div>
                          <div className="text-xs text-gray-400">{user.email}</div>
                        </div>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {tenantUsers.length === 0 && (
                    <div className="px-3 py-4 text-center text-xs text-gray-400">No other users in your organization</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || (visibility === 'shared' && sharedWith.length === 0)}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectSharingModal;
